import { animate, keyframes, style, transition, trigger } from '@angular/animations';
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { timer } from 'rxjs';
import { Orientation } from '../../../constants/orientation.constants';
import { Size } from '../../../constants/size.constants';
import { IFiltersObject } from '../../../models/common';
import { FilterService } from '../../../services/filter/filter.service';
import {
    WindowDimensions,
    WindowReferenceService
} from '../../../services/window-reference/window-reference.service';
import { FilterHelper } from '../../../utilities/filter-helper';
import { forLifeOf, LifecycleAware } from '../../../utilities/rxjs.helpers';
import { Utilities } from '../../../utilities/utilities.helper';
import { ColumnType } from '../../constants/column-type';
import { ITableColumn } from '../../models/table-column';
import { FilterType } from '../filters/constants/filter-types';

@LifecycleAware()
@Component({
    selector: 'lsq-filter-control',
    template: `
        <div
            class="filter-control-main-container"
            (mouseenter)="setIsElementHovering(true)"
            (mouseleave)="startHoverTimeout()"
        >
            <ng-content></ng-content>
            <div
                *ngIf="
                    (column.filterField && isElementHovering) || isFilterHovering || isFilterInUse
                "
                class="filter-control-container"
                (clickOutside)="isFilterInUse = false"
                (mouseleave)="setIsFilterHovering(false)"
                [@dropIn]
            >
                <div
                    class="filter-control-button rounded-circle"
                    (mouseenter)="setIsFilterHovering(true)"
                >
                    <i class="fas fa-filter"></i>
                </div>

                <div
                    *ngIf="isFilterHovering || isFilterInUse"
                    class="filter-container"
                    (click)="isFilterInUse = true"
                    [ngClass]="{
                        'flip-left': flipLeft,
                        'show-filter': showFilter
                    }"
                >
                    <div #filterBodyRef class="filter-body">
                        <lsq-number-filter
                            *ngIf="
                                filterHelper.getFilterType(column.type) === filterType.Int ||
                                filterHelper.getFilterType(column.type) === filterType.Float
                            "
                            [column]="column"
                            [isInteger]="this.column.type === this.columnType.Days"
                            [filter]="filters[column.filterField]"
                            (updated)="updateFilter($event)"
                        >
                        </lsq-number-filter>

                        <lsq-enum-filter
                            *ngIf="filterHelper.getFilterType(column.type) === filterType.Enum"
                            [filter]="filters[column.filterField]"
                            [column]="column"
                            (updated)="updateFilter($event)"
                        >
                        </lsq-enum-filter>

                        <lsq-text-filter
                            *ngIf="filterHelper.getFilterType(column.type) === filterType.Text"
                            [filter]="filters[column.filterField]"
                            [column]="column"
                            (updated)="updateFilter($event)"
                        >
                        </lsq-text-filter>

                        <lsq-date-filter
                            *ngIf="filterHelper.getFilterType(column.type) === filterType.Date"
                            [filter]="filters[column.filterField]"
                            [column]="column"
                            (updated)="updateFilter($event)"
                        >
                        </lsq-date-filter>
                    </div>
                </div>
            </div>
        </div>
    `,
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./lsq-filter-control.component.scss'],
    animations: [
        trigger('dropIn', [
            transition(':enter', [
                animate(
                    150,
                    keyframes([
                        style({ top: '-1.5rem', offset: 0 }),
                        style({ top: '-5rem', offset: 0.7 }),
                        style({ top: '-4.5rem', offset: 1.0 })
                    ])
                )
            ])
        ])
    ]
})
export class LsqFilterControlComponent implements OnInit, OnDestroy {
    @Output() public filterUpdated = new EventEmitter<IFiltersObject>();
    @Input() public column: ITableColumn;
    @Input() public filters: IFiltersObject;

    @ViewChild('filterBodyRef') public filterBodyRef: ElementRef;

    public columnType = ColumnType;
    public filterType = FilterType;
    public orientation = Orientation;
    public size = Size;
    public isElementHovering = false;
    public isFilterHovering = false;
    public isFilterInUse = false;
    public flipLeft = false;
    public componentIsActive = false;
    public showFilter = false;
    public filterHelper = FilterHelper;
    private windowWidth: number;
    private hoverTimeout: any;

    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private windowReferenceService: WindowReferenceService,
        private filterService: FilterService
    ) {}

    public ngOnInit(): void {
        this.componentIsActive = true;

        this.windowReferenceService.resizeStream
            .pipe(forLifeOf(this))
            .subscribe((window: WindowDimensions) => {
                this.showFilter = false;
                this.flipLeft = false;
                this.windowWidth = window.width;
            });

        this.filterService.filterStream.pipe(forLifeOf(this)).subscribe((currentFilter) => {
            if (currentFilter !== this.column.key) {
                this.isFilterInUse = false;
            }
        });
    }

    public ngOnDestroy(): void {
        this.componentIsActive = false;
    }

    public updateFilter(filter: IFiltersObject): void {
        this.setIsFilterHovering(false);
        this.setIsElementHovering(false);
        this.isFilterInUse = false;
        this.filterUpdated.emit(filter);
    }

    public setIsElementHovering(value: boolean): void {
        this.isElementHovering = value;
        if (value && this.hoverTimeout) {
            clearTimeout(this.hoverTimeout);
        }
    }

    public startHoverTimeout(): void {
        if (this.hoverTimeout) {
            clearTimeout(this.hoverTimeout);
        }
        // Wait some amount of time before hiding so the mouse can travel to the filter icon.
        this.hoverTimeout = timer(300)
            .pipe(forLifeOf(this))
            .subscribe(() => {
                this.setIsElementHovering(false);
                this.changeDetectorRef.markForCheck();
                this.changeDetectorRef.detectChanges();
            });
    }

    public setIsFilterHovering(value: boolean): void {
        this.isFilterHovering = value;
        if (value) {
            this.filterService.updateVisibleFilter(this.column.key);
            this.detectFlipFilter();
        }
    }

    public detectFlipFilter(): void {
        if (!this.showFilter) {
            Utilities.executeAfterRender(() => {
                if (this.filterBodyRef && this.componentIsActive) {
                    const boundingRec = this.filterBodyRef.nativeElement.getBoundingClientRect();
                    this.flipLeft = boundingRec.right > this.windowWidth;
                    this.changeDetectorRef.markForCheck();
                    this.changeDetectorRef.detectChanges();
                    this.showFilter = true;
                }
            });
        }
    }
}
